import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { DateTimeFilterPicker } from '@/components/form-components/DateTimeFilterPicker'
import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { PageHeader } from '@/components/ui/PageHeader'
import { TileFilterPopover } from '@/components/ui/TileFilterPopover'
import { Suspense, useCallback, useMemo, useState } from 'react'
import { PlatformBreadcrumbs } from './PlatformBreadcrumbs'
import { RedisMenu } from './RedisMenu'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { DataTable } from '@/components/ui/DataTable/DataTable'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { columns } from './PlatformAuditLogsTable/platformAuditLogsTableColumns'
import { IconV2FilterOutline } from '@icons/IconV2FilterOutline'

type AuditLogFilters = {
  startTime: string | undefined
  endTime: string | undefined
  affectedTable: 'user' | 'team' | undefined
  affectedRecordId: number | undefined
  userId: number | undefined
}

const DEFAULT_FILTERS: AuditLogFilters = {
  startTime: new Date(Date.now() - 50 * 60 * 1000).toISOString(), // 5 minutes ago
  endTime: new Date().toISOString(), // now
  affectedTable: undefined,
  affectedRecordId: undefined,
  userId: undefined,
}

export function PlatformAuditLogsPage() {
  return (
    <>
      <PageHeader className="pr-3" title={<PlatformBreadcrumbs title="Audit Logs" />}>
        <RedisMenu />
      </PageHeader>
      <QueryErrorBoundary errorMessage="Failed to get audit logs">
        <Suspense fallback={<LoadingComponent text="Loading audit logs..." />}>
          <PlatformAuditLogsPageContent />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}

function PlatformAuditLogsPageContent() {
  const { getSupportApiGetPlatformUsers } = useRestClient()
  const { data: users } = getSupportApiGetPlatformUsers.useSuspenseQuery(undefined)

  return (
    <>
      <TablePageContainer>
        <PlatformUsersTable users={users} />
      </TablePageContainer>
    </>
  )
}

type MessagesPlatformPlatformUser = RestSchemas['Messages.Platform.PlatformUserWithAudit']

function PlatformUsersTable({ users }: { users: MessagesPlatformPlatformUser[] }) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const handleFiltersChange = useCallback((filters: AuditLogFilters) => {
    setFilters(filters)
  }, [])
  const { getSupportApiGetAuditLogs } = useRestClient()

  const select = useCallback(
    (data: RestSchemas['Messages.Support.AuditLogEntry'][]) =>
      data.map((item) => ({
        ...item,
        user: users.find((user) => user.id === item.userId),
      })),
    [users]
  )

  const { data: auditLogs } = getSupportApiGetAuditLogs.useSuspenseQuery({ query: { ...filters } }, { select })
  return (
    <>
      <DataTable
        columns={columns}
        data={auditLogs}
        filters={<Filters filters={filters} users={users} handleFiltersChange={handleFiltersChange} />}
      />
    </>
  )
}

function Filters({
  filters,
  users,
  handleFiltersChange,
}: {
  filters: AuditLogFilters
  users: MessagesPlatformPlatformUser[]
  handleFiltersChange: (filters: AuditLogFilters) => void
}) {
  const [open, setOpen] = useState(false)
  const [draftFilters, setDraftFilters] = useState(filters)
  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: `${[user.givenName, user.familyName].filter(Boolean).join(' ') || user.email || `User ${user.id}`}`,
        description: user.email,
      })),
    [users]
  )

  return (
    <TileFilterPopover
      icon={<IconV2FilterOutline size={18} />}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) {
          setDraftFilters(filters)
        }
      }}
    >
      <div className="space-y-3">
        <div className="text-sm font-medium">Audit log filters</div>
        <div className="space-y-1.5">
          <Label>Start time</Label>
          <DateTimeFilterPicker
            value={draftFilters.startTime}
            onChange={(startTime) => setDraftFilters((prev) => ({ ...prev, startTime }))}
            placeholder="Start time"
            className="w-full"
          />
        </div>
        <div className="space-y-1.5">
          <Label>End time</Label>
          <DateTimeFilterPicker
            value={draftFilters.endTime}
            onChange={(endTime) => setDraftFilters((prev) => ({ ...prev, endTime }))}
            placeholder="End time"
            className="w-full"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Affected DB table</Label>
          <SingleSelectCombobox
            options={[
              { label: 'User', value: 'user' },
              { label: 'Team', value: 'team' },
            ]}
            selectedValue={draftFilters.affectedTable ?? null}
            onSelectionChange={(affectedTable) =>
              setDraftFilters((prev) => ({ ...prev, affectedTable: affectedTable ?? undefined }))
            }
            placeholder="Affected table"
            className="w-full"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Affected record ID</Label>
          <Input
            type="number"
            placeholder="Record ID"
            className="w-full"
            value={draftFilters.affectedRecordId ?? ''}
            onChange={(event) =>
              setDraftFilters((prev) => ({
                ...prev,
                affectedRecordId: event.target.value ? Number(event.target.value) : undefined,
              }))
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label>Made by user</Label>
          <SingleSelectCombobox
            options={userOptions}
            selectedValue={draftFilters.userId ?? null}
            onSelectionChange={(userId) => setDraftFilters((prev) => ({ ...prev, userId: userId ?? undefined }))}
            placeholder="All users"
            searchPlaceholder="Search users..."
            emptyMessage="No users found."
            className="w-full"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setDraftFilters(DEFAULT_FILTERS)}>
            Reset
          </Button>
          <Button
            size="sm"
            onClick={() => {
              handleFiltersChange(draftFilters)
              setOpen(false)
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </TileFilterPopover>
  )
}
